import { Alert, Button, Stack } from '@mui/material';
import AppConfig, { isProductionHost } from 'app-config';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { useStore } from 'store';
import { EAuthStage } from 'store/AuthStore';
import { switchEnvironment } from 'auth/environment';
import { AuthError } from 'auth/errors';

function SandboxBanner() {
  const { pathname, query } = useRouter();
  const { AuthStore: auth } = useStore();
  const [productionHost, setProductionHost] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setProductionHost(isProductionHost());
  }, []);
  // Only the production frontend offers sandbox; staging and localhost are always live.
  // Check after hydration because the server cannot read the browser selection.
  if (!productionHost) return null;
  const sandbox = AppConfig.mode === 'sandbox';
  const login =
    pathname === '/auth/login' || (pathname === '/auth/[stage]' && query.stage === 'login');
  if (!sandbox && !login) return null;
  const busy =
    switching ||
    Object.values(auth.isLoading).some(Boolean) ||
    [EAuthStage.IDLE, EAuthStage.INITIALIZING, EAuthStage.AUTHENTICATED].includes(auth.authStage);
  const switchMode = async () => {
    setSwitching(true);
    setError('');
    try {
      await switchEnvironment(sandbox ? 'live' : 'sandbox');
    } catch (failure) {
      setError(
        failure instanceof AuthError
          ? failure.message
          : 'Unable to switch environments. Check browser storage and try again.'
      );
      setSwitching(false);
    }
  };
  return (
    <Stack spacing={1}>
      <Alert
        severity={sandbox ? 'warning' : 'info'}
        sx={{
          bgcolor: sandbox ? 'warning.main' : 'primary.main',
          color: sandbox ? 'dark.main' : 'white.main',
          borderRadius: 0,
          fontWeight: 700,
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          '& .MuiAlert-icon': { color: 'inherit', opacity: 1 },
          '& .MuiAlert-action': {
            alignItems: 'center',
            ml: { xs: 4, sm: 'auto' },
            py: 0
          }
        }}
        action={
          login && (
            <Button
              color="inherit"
              variant="outlined"
              disabled={busy}
              onClick={switchMode}
              sx={{
                color: 'inherit',
                borderColor: 'currentColor',
                fontWeight: 700,
                '&:hover': {
                  color: 'inherit',
                  borderColor: 'currentColor',
                  bgcolor: 'rgba(0, 0, 0, 0.08)'
                },
                '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: 2 },
                '&.Mui-disabled': { color: 'inherit', borderColor: 'currentColor' }
              }}
            >
              {switching
                ? 'Switching…'
                : `Switch to ${sandbox ? 'Live Mode' : 'Test Mode (Sandbox)'}`}
            </Button>
          )
        }
      >
        {sandbox ? 'Test Mode (Sandbox)' : 'Live Mode'}
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

export default observer(SandboxBanner);
